// src/pages/reports/NewReport.js
import React, { useEffect, useState, useMemo } from "react";
import Layout from "../../components/Layout";
import { db } from "../../firebase";

import {
  collection,
  query,
  where,
  getDocs,
  orderBy,
} from "firebase/firestore";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { Timestamp } from "firebase/firestore";

function formatNumber(v) {
  return Number(v || 0).toFixed(2);
}

function today() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
}

function formatDDMMYYYY(ymd) {
  if (!ymd) return "";
  const [yyyy, mm, dd] = ymd.split("-");
  return `${dd}-${mm}-${yyyy}`;
}

function toStartOfDayTs(dateStr) {
  const d = new Date(dateStr);
  d.setHours(0, 0, 0, 0);
  return Timestamp.fromDate(d);
}

function toEndOfDayTs(dateStr) {
  const d = new Date(dateStr);
  d.setHours(23, 59, 59, 999);
  return Timestamp.fromDate(d);
}





export default function NewReport() {
  const [fromDate, setFromDate] = useState(today());
  const [toDate, setToDate] = useState(today());
  const [searchText, setSearchText] = useState("");

  const [loading, setLoading] = useState(false);
  const [allRows, setAllRows] = useState([]);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 50;

  const [totals, setTotals] = useState({
    Qty: 0,
    BasicAmount: 0,
    TaxAmount: 0,
    AddlCessAmount: 0,
	ewayAmount: 0,
    
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async (opts = {}) => {
    try {
      setLoading(true);

      const from = opts.fromDate || fromDate;
      const to = opts.toDate || toDate;

      const qRef = collection(db, "orders");
      const q = query(
  collection(db, "orders"),
  where("status", "==", "APPROVED"),
  where("approvedAt", ">=", toStartOfDayTs(from)),
  where("approvedAt", "<=", toEndOfDayTs(to)),
  orderBy("approvedAt", "asc")
);

      const snap = await getDocs(q);
      const map = new Map();

      snap.docs.forEach((docSnap) => {
        const o = docSnap.data();
        const date = o.orderDateOnly || today();
        const lines = Array.isArray(o.orderLines) ? o.orderLines : [];

        lines.forEach((L) => {
          const itemCode = L.itemCode || "";
          const key = `${date}__${itemCode}`;

          if (!map.has(key)) {
            map.set(key, {
              date,
              itemCode,
              brand: L.brand || "",
              Rate: Number(L.rate || 0),
              Qty: 0,
              BasicAmount: 0,
              TaxAmount: 0,
              AddlCessAmount: 0,
			  ewayAmount: 0
              
            });
          }

          const g = map.get(key);

          const qty = Number(L.qty || 0);
          const basicAmount = Number(L.basicAmount || 0);
          const gstAmount = Number(L.gstAmount || 0);
          const cessAmount = Number(L.cessAmount || 0);
          const taxAmount = gstAmount + cessAmount;
          const addlCessAmount = Number(L.addlCessAmount || 0);
          const ewayAmount = Number(L.ewayAmount || 0); // no rebate in this report

		  //const totalAmount = Number(L.totalAmount || 0); // no rebate in this report

          g.Qty += qty;
          g.BasicAmount += basicAmount;
          g.TaxAmount += taxAmount;
          g.AddlCessAmount += addlCessAmount;
          g.ewayAmount += ewayAmount; // no rebate
        });
      });

      const rows = Array.from(map.values()).sort((a, b) => {
        if (a.date < b.date) return -1;
        if (a.date > b.date) return 1;
        return a.itemCode.localeCompare(b.itemCode);
      });

      setAllRows(rows);
      computeTotals(rows);
      setPage(1);

    } catch (err) {
      console.error(err);
      alert("Error loading new report");
    } finally {
      setLoading(false);
    }
  };

  const computeTotals = (rows) => {
    const t = {
      Qty: 0,
      BasicAmount: 0,
      TaxAmount: 0,
      AddlCessAmount: 0,
	  ewayAmount: 0
      
    };

    rows.forEach((r) => {
      t.Qty += Number(r.Qty || 0);
      t.BasicAmount += Number(r.BasicAmount || 0);
      t.TaxAmount += Number(r.TaxAmount || 0);
      t.AddlCessAmount += Number(r.AddlCessAmount || 0);
      t.ewayAmount += Number(r.ewayAmount || 0);
    });

    setTotals(t);
  };

  const visibleRows = useMemo(() => {
    const txt = (searchText || "").toLowerCase();
    const filtered = allRows.filter((r) => {
      if (!txt) return true;
      return (
        (r.itemCode || "").toLowerCase().includes(txt) ||
        (r.brand || "").toLowerCase().includes(txt) ||
        (r.date || "").toLowerCase().includes(txt)
      );
    });

    const start = (page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [searchText, allRows, page]);

  const exportExcel = () => {
    const rows = allRows.map((r) => ({
      Date: r.date,
      ItemCode: r.itemCode,
      Brand: r.brand,
      Rate: r.Rate,
      Qty: r.Qty,
      BasicAmount: r.BasicAmount,
      TaxAmount: r.TaxAmount,
      AddlCessAmount: r.AddlCessAmount,
      ewayAmount: r.ewayAmount,
    }));

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "NewReport");
    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([wbout]), `NewReport_${fromDate}_to_${toDate}.xlsx`);
  };

  return (
    <Layout>
      <div className="layout-content">
        {/* HEADER */}
        <div style={{ marginBottom: 40, display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
            <div>
                <h1 style={{ fontSize: 32, marginBottom: 8, fontWeight: 900, letterSpacing: "-0.02em" }}>Daily Item Analytics</h1>
                <p style={{ color: "var(--text-muted)", fontSize: 16 }}>Consolidated SKU-level performance metrics based on approved transactions.</p>
            </div>
            <div style={{ display: "flex", gap: 12 }}>
                <button
                    onClick={exportExcel}
                    className="btn-secondary"
                    style={{ background: "var(--surface)", display: "flex", alignItems: "center", gap: 8 }}
                >
                    <span style={{ color: "var(--success)" }}>Export Pivot</span>
                </button>
                <div style={{ display: "flex", alignItems: "center", gap: 12, background: "var(--surface)", padding: "0 16px", borderRadius: 12, border: "1px solid var(--border)", height: 48 }}>
                    <input 
                        type="date" 
                        value={fromDate} 
                        onChange={e => setFromDate(e.target.value)}
                        style={{ border: "none", background: "transparent", fontSize: 14, fontWeight: 700, color: "var(--text-main)", outline: "none" }}
                    />
                    <span style={{ color: "var(--text-muted)", fontWeight: 800 }}>→</span>
                    <input 
                        type="date" 
                        value={toDate} 
                        onChange={e => setToDate(e.target.value)}
                        style={{ border: "none", background: "transparent", fontSize: 14, fontWeight: 700, color: "var(--text-main)", outline: "none" }}
                    />
                    <button 
                        onClick={() => loadData({fromDate, toDate})} 
                        style={{ marginLeft: 16, border: "none", background: "var(--primary)", color: "white", padding: "4px 12px", borderRadius: 8, fontSize: 12, fontWeight: 800, cursor: "pointer" }}
                    >
                        APPLY
                    </button>
                </div>
            </div>
        </div>

        {/* SEARCH BAR */}
        <div className="premium-card" style={{ padding: 12, marginBottom: 32, background: "var(--surface-hover)", border: "1px solid var(--border)" }}>
            <input
                type="text"
                className="form-input"
                placeholder="Query by ItemCode, Brand, or Operational Date..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                style={{ width: "100%", height: 56, fontSize: 16, background: "var(--surface)", border: "none" }}
            />
        </div>

        {/* DATA GRID */}
        <div className="premium-card" style={{ padding: 0, overflow: "hidden" }}>
            <div style={{ padding: "16px 24px", borderBottom: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: 12, fontWeight: 800, color: "var(--text-muted)", textTransform: "uppercase" }}>Audit Ledger</span>
                <span style={{ fontSize: 11, fontWeight: 600, color: "var(--primary)", background: "var(--surface-hover)", padding: "4px 12px", borderRadius: 20 }}>PAGE {page}</span>
            </div>

            <div style={{ overflowX: "auto" }}>
                <table className="enterprise-table">
                    <thead>
                        <tr>
                            <th style={{ paddingLeft: 24 }}>DATE</th>
                            <th>SKU IDENTIFIER</th>
                            <th>BRAND</th>
                            <th style={{ textAlign: "right" }}>UNIT RATE</th>
                            <th style={{ textAlign: "right" }}>QTY</th>
                            <th style={{ textAlign: "right" }}>TAXABLE VAL</th>
                            <th style={{ textAlign: "right" }}>TAX VAL</th>
                            <th style={{ textAlign: "right" }}>ADDL CESS</th>
                            <th style={{ textAlign: "right", paddingRight: 24 }}>TOTAL GRS</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan="9" style={{ padding: 100, textAlign: "center" }}>
                                    <div style={{ fontSize: 16, fontWeight: 700, color: "var(--primary)" }}>DECRYPTING DATA MATRIX...</div>
                                </td>
                            </tr>
                        ) : visibleRows.length === 0 ? (
                            <tr>
                                <td colSpan="9" style={{ padding: 80, textAlign: "center", color: "var(--text-muted)" }}>
                                    No records found for the specific query parameters.
                                </td>
                            </tr>
                        ) : (
                            visibleRows.map((r, i) => (
                                <tr key={i}>
                                    <td style={{ paddingLeft: 24, fontSize: 12, fontWeight: 700 }}>{formatDDMMYYYY(r.date)}</td>
                                    <td style={{ fontWeight: 800, color: "var(--primary)" }}>{r.itemCode}</td>
                                    <td>{r.brand}</td>
                                    <td style={{ textAlign: "right" }}>₹{formatNumber(r.Rate)}</td>
                                    <td style={{ textAlign: "right", fontWeight: 800 }}>{r.Qty.toLocaleString()}</td>
                                    <td style={{ textAlign: "right" }}>₹{Number(r.BasicAmount).toLocaleString()}</td>
                                    <td style={{ textAlign: "right" }}>₹{Number(r.TaxAmount).toLocaleString()}</td>
                                    <td style={{ textAlign: "right" }}>₹{Number(r.AddlCessAmount).toLocaleString()}</td>
                                    <td style={{ textAlign: "right", paddingRight: 24, fontWeight: 900 }}>₹{Number(r.ewayAmount).toLocaleString()}</td>
                                </tr>
                            ))
                        )}
                    </tbody>
                    <tfoot style={{ background: "var(--surface-hover)", borderTop: "2px solid var(--border)" }}>
                        <tr style={{ fontWeight: 900 }}>
                            <td colSpan="4" style={{ textAlign: "right", padding: 20 }}>SUMMARY TOTALS:</td>
                            <td style={{ textAlign: "right" }}>{totals.Qty.toLocaleString()}</td>
                            <td style={{ textAlign: "right" }}>₹{Number(totals.BasicAmount).toLocaleString()}</td>
                            <td style={{ textAlign: "right" }}>₹{Number(totals.TaxAmount).toLocaleString()}</td>
                            <td style={{ textAlign: "right" }}>₹{Number(totals.AddlCessAmount).toLocaleString()}</td>
                            <td style={{ textAlign: "right", paddingRight: 24, fontSize: 18, color: "var(--primary)" }}>₹{Number(totals.ewayAmount).toLocaleString()}</td>
                        </tr>
                    </tfoot>
                </table>
            </div>
        </div>

        {/* PAGINATION */}
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 16, marginTop: 32 }}>
            <button 
                onClick={() => setPage(Math.max(1, page - 1))}
                className="btn-secondary"
                disabled={page === 1}
                style={{ height: 40, width: 40, padding: 0, display: "flex", alignItems: "center", justifyContent: "center" }}
            >
                ←
            </button>
            <div style={{ fontSize: 14, fontWeight: 800, color: "var(--text-main)" }}>PAGE {page}</div>
            <button 
                onClick={() => setPage(page + 1)}
                className="btn-secondary"
                disabled={visibleRows.length < PAGE_SIZE}
                style={{ height: 40, width: 40, padding: 0, display: "flex", alignItems: "center", justifyContent: "center" }}
            >
                →
            </button>
        </div>
      </div>
    </Layout>
  );
}
